// What the autofill section configures: a deal workflow question (the default) or a
// contract onboarding workflow item. settingsapi serves the same five autofill endpoints
// under each field collection, so only the routes, the draft payload, the record a test
// runs against and a few entity-worded strings differ.
import { createContext, useContext } from "react";
import type { CreateQuestionnaireQuestionSettings } from "@/pages/deals/types";

export type AutofillTargetKind = "dealQuestion" | "contractWorkflowItem";

export interface AutofillDraftField {
  groupId: string;
  name: string;
  fieldType: string;
  helpText: string | null;
}

export interface AutofillTarget {
  kind: AutofillTargetKind;
  routes: {
    createField: string;
    field: string;
    instructions: string;
    testRuns: string;
    testRunById: string;
  };
  /** The field id's path param name in `routes`. */
  fieldIdParam: string;
  /** Body that creates the hidden draft field instructions are generated against. */
  buildDraft: (field: AutofillDraftField) => Record<string, unknown>;
  /** Whether the test button also needs the deal autofill-test rollout flag. */
  requiresTestFlag: boolean;
  /** Whether the admin can change which activity types trigger autofill. */
  triggerTypesEditable: boolean;
  /** Keys, relative to the section's translation prefix, of the entity-worded strings. */
  copyKeys: {
    tooltip: string;
    recordLabel: string;
    recordPlaceholder: string;
    noResult: string;
  };
}

export const DEAL_QUESTION_AUTOFILL_TARGET: AutofillTarget = {
  kind: "dealQuestion",
  routes: {
    createField: "settings/createQuestionnaireQuestion",
    field: "settings/updateQuestionnaireQuestion",
    instructions: "settings/autofillInstructions",
    testRuns: "settings/autofillTestRuns",
    testRunById: "settings/autofillTestRunById"
  },
  fieldIdParam: "questionId",
  buildDraft: ({ groupId, name, fieldType, helpText }) =>
    ({
      questionnaireId: groupId,
      questionText: name,
      responseType: {
        value: "none",
        type: fieldType,
        setValueType: null,
        unit: null
      },
      questionOrder: null,
      isMandatory: false,
      isSuccessCriteria: false,
      tooltipText: helpText,
      filledByDreamer: false,
      isDraft: true
    }) satisfies Partial<CreateQuestionnaireQuestionSettings> & {
      questionnaireId: string;
      questionText: string;
      isDraft: true;
    },
  requiresTestFlag: true,
  triggerTypesEditable: false,
  copyKeys: {
    tooltip: "tooltip",
    recordLabel: "test.dealLabel",
    recordPlaceholder: "test.dealPlaceholder",
    noResult: "test.noResult"
  }
};

// Unprovided, the section configures deal questions.
export const AutofillTargetContext = createContext<AutofillTarget>(
  DEAL_QUESTION_AUTOFILL_TARGET
);

export const useAutofillTarget = (): AutofillTarget =>
  useContext(AutofillTargetContext);
